<svelte:options
  customElement={{
    tag: "oc-tab-item-v1",
    shadow: "none",
    props: {
      disabled: { type: "Boolean" },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import { waitForNextAnimationFrame } from "@otto-ec/otto-components-utils/utils/waitForNextAnimationFrame";
  import type { TabItemProps } from "./TabBarV1.types.js";
  import { useOverflowCalc } from "./composables/useOverflowCalc.svelte.js";
  import { tooltipContent } from "./lib/utils.js";

  let { disabled = false }: TabItemProps = $props();

  const Host = $host();
  let slottedElement = $state<HTMLElement | null>(null);
  let boldWidth = $state<number | undefined>(undefined);
  let textWrapper = $state<HTMLElement | null>(null);
  let hasTooltip = $state(false);

  const overflowState = useOverflowCalc();

  function handleClick(event: Event): void {
    if (disabled) {
      event.preventDefault();
      return;
    }

    const index = Host.getAttribute("data-index");
    if (index !== null) {
      /*                                                 */
      Host.dispatchEvent(
        new CustomEvent("oc-tab-select", {
          bubbles: true,
          composed: true,
          detail: { index },
        }),
      );
    }
  }

  function updateDisabledState(): void {
    if (!slottedElement) return;

    if (disabled) {
      slottedElement.parentElement?.setAttribute("disabled", "");
      slottedElement.setAttribute("aria-disabled", "true");
      slottedElement.setAttribute("tabindex", "-1");
    } else {
      slottedElement.removeAttribute("aria-disabled");
    }
  }

  /**
 *
 *
 */
  async function calculateBoldWidth(): Promise<void> {
    if (!textWrapper || boldWidth !== undefined) return;

    /*                                           */
    const originalFontWeight = textWrapper.style.fontWeight;

    textWrapper.style.fontWeight = "700"; /*          */

    await waitForNextAnimationFrame();
    boldWidth = textWrapper.getBoundingClientRect().width;

    /*                                             */
    if (boldWidth === 0) {
      await waitForNextAnimationFrame();
      boldWidth = textWrapper.getBoundingClientRect().width;
    }

    if (boldWidth === 0) {
      await waitForNextAnimationFrame();
      boldWidth = textWrapper.getBoundingClientRect().width;
    }

    /*                           */
    textWrapper.style.fontWeight = originalFontWeight;

    /*                                                   */
    if (boldWidth > 0) {
      textWrapper.style.minWidth = `${boldWidth}px`;
    }
  }

  /**
 *
 *
 */
  function wrapContentInSpan(element: HTMLElement): void {
    if (element.querySelector(".tab-item-text")) {
      textWrapper = element.querySelector(".tab-item-text");
      overflowState.innerSpan = textWrapper;
      return;
    }

    const wrapper = document.createElement("span");
    wrapper.className = "tab-item-text";

    const childNodes = Array.from(element.childNodes);
    childNodes.forEach((node) => {
      /*                                                         */
      if (node.nodeName === "OC-ICON-V1" || node.nodeName === "OC-BADGE-V2") {
        return;
      }
      /*                                */
      wrapper.appendChild(node);
    });

    if (wrapper.childNodes.length > 0) {
      element.appendChild(wrapper);
      textWrapper = wrapper;
      overflowState.innerSpan = wrapper;

      /*                                  */
      calculateBoldWidth();
    }
  }

  onMount(() => {
    slottedElement = Host.querySelector("button, a") as HTMLElement;

    if (slottedElement) {
      /*                                  */
      wrapContentInSpan(slottedElement);

      /*                                                                                     */
      const overlay = document.createElement("oc-interactive-overlay-v2");
      slottedElement.parentNode?.insertBefore(overlay, slottedElement);
      overlay.appendChild(slottedElement);

      /*                                  */
      slottedElement.setAttribute("role", "tab");
      slottedElement.setAttribute("aria-selected", "false");
      slottedElement.setAttribute("tabindex", "-1");

      /*                            */
      slottedElement.addEventListener("click", handleClick);

      /*                              */
      slottedElement.addEventListener("mouseover", overflowState.computeOverflow);
      slottedElement.addEventListener("touchstart", overflowState.computeOverflow);

      /*                                                                 */
      const resizeObserver = new ResizeObserver(() => {
        overflowState.computeOverflow();
      });

      if (textWrapper) {
        resizeObserver.observe(textWrapper);
      }
      resizeObserver.observe(Host);

      updateDisabledState();

      /*                                                         */
      requestAnimationFrame(() => {
        overflowState.computeOverflow();
      });

      return () => {
        slottedElement?.removeEventListener("click", handleClick);
        slottedElement?.removeEventListener("mouseover", overflowState.computeOverflow);
        slottedElement?.removeEventListener("touchstart", overflowState.computeOverflow);
        resizeObserver.disconnect();
      };
    }
  });

  $effect(() => {
    updateDisabledState();
  });

  /*                                             */
  $effect(() => {
    const isOverflowing = overflowState.value;

    if (isOverflowing && !hasTooltip && slottedElement) {
      const popover = document.createElement("oc-popover-v1");
      popover.variant = "tooltip";
      popover.innerHTML = tooltipContent(slottedElement);
      popover.position = "top";
      popover.anchor = "previous-sibling";

      Host.appendChild(popover);
      hasTooltip = true;
    } else if (!isOverflowing && hasTooltip) {
      /*                                */
      const popover = Host.querySelector("oc-popover-v1");
      if (popover) {
        popover.remove();
        hasTooltip = false;
      }
    }
  });
</script>
